import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { base_url } from "../utils/axiosConfig";

const roleContent = {
  member: {
    title: "LifeOnPlus Member",
  },
  channelpartner: {
    title: "Channel Partner",
  },
  franchise: {
    title: "LifeOnPlus Franchise",
  },
  channelpartnerplus: {
    title: "Channel Partner Plus",
  },
  corporate: {
    title: "Corporate Partnership",
  },
  director: {
    title: "Director / Super Distributor",
  },
  vendor: {
    title: "Vendor Marketplace",
  },
  enduser: {
    title: "End User",
  },
};

// Validation Schemas
const leadSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name is too short")
    .required("Full Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Enter valid 10-digit Indian mobile number")
    .required("Phone number is required"),
});

const otpSchema = Yup.object().shape({
  otp: Yup.string()
    .matches(/^\d{6}$/, "OTP must be 6 digits")
    .required("OTP is required"),
});

const RoleInfoModal = ({ show, handleClose, role }) => {
  const data = roleContent[role] || roleContent["member"];
  const [step, setStep] = useState(1); // 1 = Form, 2 = OTP, 3 = Download
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [pdfPath, setPdfPath] = useState("/pdfs/brochure.pdf");

  useEffect(() => {
    if (!role) {
      setPdfPath("/pdfs/brochure.pdf");
      return;
    }

    const file = `/pdfs/${role}.pdf`;

    fetch(file, { method: "HEAD" })
      .then((res) => {
        if (res.ok) {
          setPdfPath(file);
        } else {
          setPdfPath("/pdfs/brochure.pdf");
        }
      })
      .catch(() => {
        setPdfPath("/pdfs/brochure.pdf");
      });
  }, [role]);

  const getRelatedTo = (roleKey) => {
    const map = {
      member: "member",
      channelpartner: "channelPartner",
      channelpartnerplus: "channelPartnerPlus",
      franchise: "franchise",
      corporate: "corporate",
      director: "director",
      vendor: "vendor",
      enduser: "endUser",
    };
    return map[roleKey] || "member";
  };

  const getStorageKey = () => `brochure_verified_${role}`;

  // Check localStorage when modal opens
  useEffect(() => {
    if (show) {
      const verifiedData = localStorage.getItem(getStorageKey());
      if (verifiedData) {
        try {
          const parsed = JSON.parse(verifiedData);
          setFormData({
            name: parsed.name || "",
            email: parsed.email || "",
            phone: parsed.phone || "",
          });
          setStep(3); // Go directly to Download
        } catch (e) {
          localStorage.removeItem(getStorageKey());
          setStep(1);
        }
      } else {
        setStep(1);
      }
    }
  }, [show, role]);

  // ===================== SEND OTP =====================
  const sendOtp = async (values) => {
    setLoading(true);
    setError("");

    try {
      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        relatedTo: getRelatedTo(role),
      };

      const res = await axios.post(
        `${base_url}v1/user/sendOtpForBrochure`,
        payload,
      );

      if (res.data?.alreadyVerified) {
        const verifiedInfo = {
          name: res.data.data?.name || values.name,
          email: res.data.data?.email || values.email,
          phone: res.data.data?.phone || values.phone,
          role: role,
          verifiedAt: new Date().toISOString(),
        };

        localStorage.setItem(getStorageKey(), JSON.stringify(verifiedInfo));
        setFormData(verifiedInfo);
        setStep(3);
        return;
      }

      setFormData(values);
      setStep(2);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send OTP. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // ===================== VERIFY OTP =====================
  const verifyOtp = async (values) => {
    setLoading(true);
    setError("");

    try {
      const payload = {
        email: formData.email,
        phone: formData.phone,
        otp: values.otp,
        relatedTo: getRelatedTo(role),
      };

      await axios.post(`${base_url}v1/user/verifyOtpForBrochure`, payload);

      const verifiedInfo = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: role,
        verifiedAt: new Date().toISOString(),
      };
      localStorage.setItem(getStorageKey(), JSON.stringify(verifiedInfo));

      setStep(3);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Invalid OTP. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // ===================== RESEND OTP =====================
  const handleResendOtp = async () => {
    if (!formData.email || !formData.phone) return;

    setLoading(true);
    setError("");

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        relatedTo: getRelatedTo(role),
      };

      const res = await axios.post(
        `${base_url}v1/user/sendOtpForBrochure`,
        payload,
      );

      if (res.data?.alreadyVerified) {
        const verifiedInfo = {
          name: res.data.data?.name || formData.name,
          email: res.data.data?.email || formData.email,
          phone: res.data.data?.phone || formData.phone,
          role: role,
          verifiedAt: new Date().toISOString(),
        };
        localStorage.setItem(getStorageKey(), JSON.stringify(verifiedInfo));
        setStep(3);
        return;
      }

      setError("");
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to resend OTP. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setFormData({ name: "", email: "", phone: "" });
    setError("");
    handleClose();
  };

  return (
    <Modal show={show} onHide={resetModal} centered>
      <Modal.Header closeButton className="py-2">
        <Modal.Title
          style={{ color: "#02BE0F", fontWeight: 600, fontSize: "1.15rem" }}
        >
          {step === 1 && data.title}
          {step === 2 && "Verify OTP"}
          {step === 3 && "Download Brochure"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-3 py-3" style={{ background: "#f8f9fa" }}>
        {/* ========== STEP 1: Form ========== */}
        {step === 1 && (
          <div className="bg-white p-3 rounded-3">
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              Fill the form to download the brochure. Our team will also connect
              with you.
            </p>

            <Formik
              initialValues={{ name: "", email: "", phone: "" }}
              validationSchema={leadSchema}
              onSubmit={sendOtp}
            >
              {({ isSubmitting }) => (
                <FormikForm>
                  <Form.Group className="mb-2">
                    <Form.Label
                      className="mb-1"
                      style={{ fontSize: "0.85rem", fontWeight: 500 }}
                    >
                      Full Name *
                    </Form.Label>
                    <Field
                      name="name"
                      className="form-control form-control-sm"
                      placeholder="Enter your full name"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger"
                      style={{ fontSize: "0.75rem" }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label
                      className="mb-1"
                      style={{ fontSize: "0.85rem", fontWeight: 500 }}
                    >
                      Email Address *
                    </Form.Label>
                    <Field
                      name="email"
                      type="email"
                      className="form-control form-control-sm"
                      placeholder="Enter your email"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger"
                      style={{ fontSize: "0.75rem" }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label
                      className="mb-1"
                      style={{ fontSize: "0.85rem", fontWeight: 500 }}
                    >
                      Phone Number *
                    </Form.Label>
                    <Field
                      name="phone"
                      type="tel"
                      className="form-control form-control-sm"
                      placeholder="10-digit mobile number"
                    />
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-danger"
                      style={{ fontSize: "0.75rem" }}
                    />
                  </Form.Group>

                  {error && (
                    <p
                      className="text-danger mb-2"
                      style={{ fontSize: "0.8rem" }}
                    >
                      {error}
                    </p>
                  )}

                  <div className="d-grid mt-3">
                    <Button
                      type="submit"
                      size="sm"
                      style={{
                        background: "#02BE0F",
                        border: "none",
                        fontWeight: 500,
                      }}
                      disabled={isSubmitting || loading}
                    >
                      {isSubmitting || loading ? (
                        <Spinner size="sm" />
                      ) : (
                        "Send OTP"
                      )}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        )}

        {/* ========== STEP 2: OTP ========== */}
        {/* ========== STEP 2: OTP ========== */}
        {step === 2 && (
          <div className="bg-white p-3 rounded-3">
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              We have sent the <strong>same OTP</strong> to your Email and
              Mobile Number.
            </p>

            <Formik
              initialValues={{ otp: "" }}
              validationSchema={otpSchema}
              onSubmit={verifyOtp}
            >
              {({ isSubmitting }) => (
                <FormikForm>
                  <Form.Group className="mb-2">
                    <Form.Label
                      className="mb-1 d-block text-center"
                      style={{ fontSize: "0.85rem", fontWeight: 500 }}
                    >
                      Enter OTP
                    </Form.Label>

                    {/* Compact OTP Input */}
                    <div className="d-flex justify-content-center">
                      <Field
                        name="otp"
                        className="form-control form-control-sm text-center"
                        placeholder="• • • • • •"
                        maxLength={6}
                        style={{
                          width: "160px",
                          letterSpacing: "6px",
                          fontSize: "1.15rem",
                          fontWeight: 500,
                          padding: "6px 10px",
                        }}
                      />
                    </div>

                    <ErrorMessage
                      name="otp"
                      component="div"
                      className="text-danger text-center mt-1"
                      style={{ fontSize: "0.75rem" }}
                    />
                  </Form.Group>

                  {error && (
                    <p
                      className="text-danger text-center mb-2"
                      style={{ fontSize: "0.8rem" }}
                    >
                      {error}
                    </p>
                  )}

                  <div className="text-center mb-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-decoration-none p-0"
                      onClick={handleResendOtp}
                      disabled={loading}
                      style={{ fontSize: "0.8rem" }}
                    >
                      {loading ? "Sending..." : "Resend OTP"}
                    </button>
                  </div>

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="flex-fill"
                      onClick={() => setStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      size="sm"
                      className="flex-fill"
                      style={{ background: "#02BE0F", border: "none" }}
                      disabled={isSubmitting || loading}
                    >
                      {isSubmitting || loading ? (
                        <Spinner size="sm" />
                      ) : (
                        "Verify OTP"
                      )}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        )}

        {/* ========== STEP 3: Download ========== */}
        {step === 3 && (
          <div className="bg-white p-4 rounded-3 text-center">
            <div style={{ fontSize: "2.8rem" }} className="mb-2">
              📄
            </div>
            <h5 className="mb-2" style={{ color: "#02BE0F", fontWeight: 600 }}>
              {localStorage.getItem(getStorageKey())
                ? "Welcome Back!"
                : "Verification Successful!"}
            </h5>
            <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
              Your brochure is ready. Click below to download.
            </p>
            <a
              href={pdfPath}
              download
              className="btn btn-sm px-4 text-white"
              style={{ background: "#02BE0F", border: "none", fontWeight: 500 }}
            >
              Download PDF
            </a>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RoleInfoModal;
